import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import LoadingScreen from "./LoadingScreen";
import toast from "react-hot-toast";
import { useAuth } from "../context/AuthContext";
import { useOrganization } from "../context/OrganizationContext";
import { motion } from "framer-motion";
import { LOGO_SRC } from "../constants/branding";
import { normalizeAuthApiErrors } from "../utils/normalizeAuthApiErrors";
import GoogleSignInButton from "./GoogleSignInButton";
import useThrottleCooldown from "../hooks/useThrottleCooldown";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:8002";

const Login = () => {
  const { login } = useAuth();
  const { reloadOrganizations } = useOrganization();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState({});
  const cooldown = useThrottleCooldown();

  const validateForm = () => {
    const newErrors = {};
    if (!email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Email is invalid";
    }
    if (!password) {
      newErrors.password = "Password is required";
    } else if (password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          username: email,
          password: password,
        }),
      });

      let data = {};
      try {
        data = await response.json();
      } catch {
        data = {};
      }

      if (!response.ok) {
        // 429 — throttled. Start the cooldown timer using Retry-After
        // (DRF sets the header; fall back to parsing "X seconds" from
        // the body if a proxy strips it).
        if (response.status === 429) {
          const hv = Number(response.headers?.get?.("Retry-After"));
          const m = /(\d+)\s*seconds?/i.exec(
            typeof data?.detail === "string" ? data.detail : "",
          );
          const retryAfter = Number.isFinite(hv) && hv > 0
            ? hv
            : (m ? Number(m[1]) : 60);
          cooldown.start(retryAfter);
          const friendly = `Too many login attempts. Try again in ${
            retryAfter >= 60
              ? `${Math.ceil(retryAfter / 60)} min`
              : `${retryAfter}s`
          }.`;
          setErrors({ submit: friendly });
          toast.error(friendly, { duration: 6000 });
          return;
        }

        const normalized = normalizeAuthApiErrors(data);
        const combined =
          `${normalized.submit || ""} ${normalized.email || ""}`.toLowerCase();

        // Backend returns explicit code='email_not_verified' for inactive users
        // (status 403). Older responses with 401 + verify-keyword are also handled.
        const isEmailNotVerified =
          data?.code === "email_not_verified" ||
          response.status === 403 ||
          (response.status === 401 &&
            (combined.includes("verify") ||
              combined.includes("inactive") ||
              combined.includes("not verified")));

        if (isEmailNotVerified) {
          localStorage.setItem(
            "pendingVerificationEmail",
            data?.email || email
          );
          const msg =
            data?.detail ||
            "Your email is not verified yet. Please check your inbox for the verification link.";
          setErrors({ ...normalized, submit: msg });
          toast.error(msg, { duration: 10000 });
          // Send the user straight to the verify page with a clear path
          // forward (resend or back to login).
          navigate("/verify");
          return;
        }

        setErrors(normalized);
        return;
      }

      if (!data.token) {
        console.error("No token in response:", data);
        throw new Error("No token received from server");
      }

      // Store token and email
      console.log("Storing token and email...");
      localStorage.setItem("token", data.token);
      localStorage.setItem("userEmail", email);

      // Verify token is stored
      const storedToken = localStorage.getItem("token");
      console.log(
        "Stored token:",
        storedToken ? "Token exists" : "No token found",
      );

      if (!storedToken) {
        throw new Error("Failed to store authentication token");
      }

      // Call the login function from AuthContext
      console.log("Calling login function from AuthContext...");

      // Crear objeto de usuario
      const userData = {
        email: email,
        token: data.token,
        name: data.name || email.split("@")[0],
        role: data.role || "user",
        id: data.user_id || Date.now(),
      };

      // Guardar en cookies con useSession
      document.cookie = `user_session=${encodeURIComponent(
        JSON.stringify(userData),
      )};path=/;max-age=${60 * 60 * 24 * 7}`;
      document.cookie = `auth_token=${data.token};path=/;max-age=${
        60 * 60 * 24 * 7
      }`;

      await login(userData, data.token); // Pasar userData completo

      // Reload organizations for the newly logged-in user
      await reloadOrganizations();

      toast.success("Login successful!");

      // If the user picked a plan on the landing page before signing in,
      // hand off to Stripe checkout now. Otherwise, redirect to the
      // attempted URL or dashboard.
      const from = location.state?.from?.pathname || "/dashboard";
      const { consumePendingCheckout } = await import(
        "../utils/pendingCheckout"
      );
      await consumePendingCheckout(from, navigate);
    } catch (error) {
      console.error("Login error:", error);
      localStorage.removeItem("token");
      localStorage.removeItem("userEmail");

      const errorMessage =
        error.message || "Login failed. Please check your connection.";
      setErrors({ submit: errorMessage });
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <LoadingScreen
        title="Signing you in"
        subtitle="Preparing your workspace…"
      />
    );
  }

  return (
    <div className="relative flex min-h-screen overflow-hidden bg-[#131313] text-white">
      {/* Landing-aligned ambient background (Index.jsx uses #131313; hero uses subtle depth) */}
      <div className="pointer-events-none fixed inset-0 z-0" aria-hidden="true">
        <div className="absolute inset-0 bg-[#131313]" />
        <div className="absolute -left-1/4 top-0 h-[min(70vh,520px)] w-[min(90vw,720px)] rounded-full bg-primary/[0.07] blur-[100px]" />
        <div className="absolute bottom-0 right-0 h-[min(50vh,400px)] w-[min(80vw,560px)] rounded-full bg-emerald-500/[0.05] blur-[90px]" />
      </div>

      <div className="relative z-10 flex w-full items-center justify-center px-4 py-12 sm:px-6 ">
        <motion.div
          className="w-full max-w-lg"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: "easeOut" }}
        >
          <motion.div
            className="mb-8 flex justify-start"
            initial={{ opacity: 0, x: -12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.08, duration: 0.45, ease: "easeOut" }}
          >
            <Link
              to="/"
              className="font-secondary inline-flex items-center rounded-sm px-2 py-2 text-[15px] text-white/65 transition-colors hover:bg-white/[0.06] hover:text-white"
            >
              <svg
                className="mr-2 h-5 w-5 shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              Back to Home
            </Link>
          </motion.div>

          <motion.div
            className="rounded-2xl shadow-xl bg-[#48484826] p-8 sm:p-10 "
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.12, duration: 0.5, ease: "easeOut" }}
          >
            <motion.div
              className="mb-8 text-center"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.18, duration: 0.45, ease: "easeOut" }}
            >
              <img
                src={LOGO_SRC}
                alt="DataNodes"
                width={127}
                height={41}
                className="mx-auto mb-6 h-10 w-auto sm:h-16"
              />
              <h2 className="font-title text-2xl font-bold tracking-tight text-white sm:text-[28px]">
                Welcome back
              </h2>
              <p className="font-secondary mt-2 text-[17px] leading-relaxed text-white/65">
                Sign in to your DataNodes account
              </p>
            </motion.div>

            {errors.submit && (
              <div
                className="font-secondary mb-6 rounded-sm border border-red-500/25 bg-red-500/10 px-4 py-3 text-sm text-red-300"
                role="alert"
              >
                {errors.submit}
              </div>
            )}

            <motion.div
              className="mb-6 space-y-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.45 }}
            >
              <GoogleSignInButton disabled={isLoading} />
              <div className="flex items-center gap-3 text-xs uppercase tracking-wider text-white/40">
                <span className="h-px flex-1 bg-white/10" aria-hidden="true" />
                <span className="font-secondary">or sign in with email</span>
                <span className="h-px flex-1 bg-white/10" aria-hidden="true" />
              </div>
            </motion.div>

            <motion.form
              className="space-y-4"
              onSubmit={handleSubmit}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.22, duration: 0.45 }}
            >
              <div>
                <label
                  htmlFor="email"
                  className="font-secondary mb-2 block text-sm font-medium text-white/80"
                >
                  Email address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setErrors((prev) => {
                      const next = { ...prev, email: undefined };
                      delete next.submit;
                      return next;
                    });
                  }}
                  required
                  className={`font-secondary block w-full rounded-sm px-4 border py-3.5 text-[15px] text-white transition-colors placeholder:text-white/40 focus:outline-none focus:ring-1 ${
                    errors.email
                      ? "border-red-500/80 bg-red-500/10 focus:border-red-500 focus:ring-red-500/30"
                      : "border-[#4848481A] bg-[#1F1F1F] hover:border-white/25 focus:border-primary focus:ring-primary/30"
                  }`}
                  placeholder="Enter your email"
                  autoComplete="email"
                />
                {errors.email && (
                  <p className="mt-2 text-sm text-red-400">{errors.email}</p>
                )}
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="font-secondary mb-2 block text-sm font-medium text-white/80"
                >
                  Password
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setErrors((prev) => {
                      const next = { ...prev, password: undefined };
                      delete next.submit;
                      return next;
                    });
                  }}
                  required
                  className={`font-secondary block w-full rounded-sm border px-4 py-3.5 text-[15px] text-white transition-colors placeholder:text-white/40 focus:outline-none focus:ring-1 ${
                    errors.password
                      ? "border-red-500/80 bg-red-500/10 focus:border-red-500 focus:ring-red-500/30"
                      : "border-[#4848481A] bg-[#1F1F1F] hover:border-white/25 focus:border-primary focus:ring-primary/30"
                  }`}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                />
                {errors.password && (
                  <p className="mt-2 text-sm text-red-400">{errors.password}</p>
                )}
                <div className="mt-2 flex justify-end">
                  <Link
                    to="/forgot-password"
                    className="font-secondary text-sm text-white/65 transition-colors hover:text-primary"
                  >
                    Forgot password?
                  </Link>
                </div>
              </div>

              <motion.button
                type="submit"
                disabled={isLoading || cooldown.isCoolingDown}
                className="font-secondary mt-2 flex w-full items-center justify-center gap-2 rounded-sm border border-primary bg-primary px-6 py-4 text-[15px] font-bold leading-none text-[#0a0a0a] transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
                whileHover={isLoading || cooldown.isCoolingDown ? {} : { scale: 1.01 }}
                whileTap={isLoading || cooldown.isCoolingDown ? {} : { scale: 0.99 }}
              >
                {isLoading ? (
                  <>
                    <span
                      className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-[#0a0a0a] border-t-transparent"
                      aria-hidden="true"
                    />
                    Signing in...
                  </>
                ) : cooldown.isCoolingDown ? (
                  cooldown.label("Try again in")
                ) : (
                  "Sign in"
                )}
              </motion.button>
            </motion.form>

            <motion.div
              className="mt-8 text-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.28, duration: 0.45 }}
            >
              <p className="font-secondary text-[15px] text-white/65">
                Don&apos;t have an account?{" "}
                <Link
                  to="/signup"
                  className="font-semibold text-primary transition-colors hover:text-primary-hover"
                >
                  Sign up
                </Link>
              </p>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default Login;
